import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/customSupabaseClient';

/**
 * "Your place" — what this account owns from the presale.
 *
 * Reads the caller's own rows from v2.purchases (RLS: purchases_select_own) joined to
 * the tier. Lives in the "Your place" tab on /profile; ProfilePage only shows that tab
 * when the account has at least one non-expired purchase (Patrick, 9/12: the full card
 * up front was too much). Expired holds are hidden here too.
 */

type Row = {
  id: string;
  status: string;
  amount_cents: number;
  brick_inscription: string | null;
  created_at: string;
  tiers: {
    display_name: string;
    credits_granted: number;
    brick_count: number;
    brick_types: string[];
    grants_membership: string | null;
  } | null;
};

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 === 0 ? 0 : 2 });

const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function statusLabel(status: string): { text: string; tone: 'ok' | 'wait' | 'off' } {
  switch (status) {
    case 'active':
      return { text: 'Paid', tone: 'ok' };
    case 'processing':
      return { text: 'Bank transfer settling', tone: 'wait' };
    case 'pending':
      return { text: 'Payment not finished', tone: 'wait' };
    case 'refunded':
      return { text: 'Refunded', tone: 'off' };
    case 'partially_refunded':
      return { text: 'Partly refunded', tone: 'off' };
    case 'disputed':
      return { text: 'Under review', tone: 'off' };
    case 'failed':
      return { text: 'Bank transfer returned', tone: 'off' };
    default:
      return { text: status, tone: 'off' };
  }
}

function whatYouGet(t: NonNullable<Row['tiers']>): string {
  const parts: string[] = [];
  if (t.credits_granted > 0) parts.push(`${t.credits_granted} ${t.credits_granted === 1 ? 'visit' : 'visits'}`);
  if (t.brick_count === 2) parts.push('Home Brick + Hull Brick');
  else if (t.brick_types.includes('hull')) parts.push('Hull Brick');
  else if (t.brick_types.includes('home')) parts.push('Home Brick');
  if (t.grants_membership === 'founding_member') parts.push('Founding membership');
  return parts.join(' · ');
}

export default function ProfilePurchases({ userId }: { userId: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('purchases')
      .select('id, status, amount_cents, brick_inscription, created_at, tiers(display_name, credits_granted, brick_count, brick_types, grants_membership)')
      .eq('user_id', userId)
      .neq('status', 'expired')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('purchases load error:', error.message);
          setRows([]);
          return;
        }
        setRows((data ?? []) as unknown as Row[]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!rows || rows.length === 0) return null;

  const paidBricks = rows.filter((r) => r.status === 'active' && (r.tiers?.brick_count ?? 0) > 0).length;

  // Rendered inside the profile's tab surface (the "Your place" tab), so no card of its own.
  return (
    <section>
      <div>
        <h2 className="text-2xl text-maslow-navy mb-5" style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}>
          {paidBricks > 0 ? 'You are in the building.' : 'Before the doors open.'}
        </h2>

        <ul className="divide-y divide-[rgba(28,43,58,0.10)]">
          {rows.map((r) => {
            const s = statusLabel(r.status);
            const toneClass =
              s.tone === 'ok'
                ? 'text-maslow-navy'
                : s.tone === 'wait'
                  ? 'text-[color-mix(in_srgb,var(--navy)_65%,transparent)]'
                  : 'text-[color-mix(in_srgb,var(--navy)_50%,transparent)] line-through';
            return (
              <li key={r.id} className="py-4 flex flex-col gap-1.5 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <p className="text-maslow-navy text-base" style={{ fontFamily: 'var(--serif)' }}>
                    {r.tiers?.display_name ?? 'Purchase'}
                    <span className="text-[color-mix(in_srgb,var(--navy)_55%,transparent)] text-sm ml-2">{when(r.created_at)}</span>
                  </p>
                  {r.tiers && (
                    <p className="text-[13px] text-[color-mix(in_srgb,var(--navy)_70%,transparent)]">{whatYouGet(r.tiers)}</p>
                  )}
                  {r.brick_inscription && (
                    <pre
                      className="mt-2 text-[13px] leading-5 text-maslow-navy bg-[rgba(28,43,58,0.04)] border border-[rgba(28,43,58,0.10)] rounded-sm px-3 py-2 inline-block whitespace-pre"
                      style={{ fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: 1 }}
                    >
                      {r.brick_inscription}
                    </pre>
                  )}
                </div>
                <div className="flex items-baseline gap-3 md:flex-col md:items-end md:gap-0.5 flex-shrink-0">
                  <span className="text-maslow-navy text-base">{money(r.amount_cents)}</span>
                  <span className={`text-[11px] uppercase tracking-[0.18em] ${toneClass}`}>{s.text}</span>
                </div>
              </li>
            );
          })}
        </ul>

        <p className="mt-4 text-[12px] text-[color-mix(in_srgb,var(--navy)_60%,transparent)]">
          Visits are good from the day we open. Questions or a change to an inscription:{' '}
          <a href="mailto:support@maslow.nyc" className="underline">support@maslow.nyc</a>.{' '}
          <Link to="/build" className="underline">The build</Link>
        </p>
      </div>
    </section>
  );
}
